/**
 * Die Logik hinter der Stammdaten-Tabelle, ohne Oberfläche.
 *
 * Bewusst frei von `document`: Zeilen bauen, suchen, filtern und
 * Sammeländerungen rechnen sind die Teile, die stimmen müssen.
 */

import { SPEICHER, alleTauchgaenge, alleAus } from './db.js';
import { register, spalte, FLASCHE } from './felder.js';
import { hatKoordinaten } from './geo.js';
import { dauer, datum, tiefe, wort } from './format.js';
import { findeOderLege, vergebeneNummern } from './formular.js';
import { speichere } from './saetze.js';

/** Wie oft wird jeder Satz eines Registers benutzt? */
function zaehle(registerSchluessel, saetze, tauchgaenge) {
  const zahl = new Map(saetze.map((s) => [s.id, 0]));
  if (registerSchluessel === 'sites') {
    for (const tg of tauchgaenge) {
      if (zahl.has(tg.site_id)) zahl.set(tg.site_id, zahl.get(tg.site_id) + 1);
    }
  } else if (registerSchluessel === 'trips') {
    for (const tg of tauchgaenge) {
      if (zahl.has(tg.trip_id)) zahl.set(tg.trip_id, zahl.get(tg.trip_id) + 1);
    }
  } else if (registerSchluessel === 'buddies') {
    // Buddys stehen als Namen im Tauchgang, nicht als Verweis.
    const nachName = new Map(saetze.map((s) => [String(s.name ?? '').toLowerCase(), s.id]));
    for (const tg of tauchgaenge) {
      for (const name of tg.buddies ?? []) {
        const id = nachName.get(String(name).toLowerCase());
        if (id) zahl.set(id, zahl.get(id) + 1);
      }
    }
  }
  return zahl;
}

/**
 * Der gespeicherte Wert einer Zelle — das, was bearbeitet wird.
 *
 * Nicht dasselbe wie das, was dasteht: Eine Auswahlspalte zeigt "Salzwasser"
 * und speichert `salt`. Wer den Wert aus der Zelle zurückliest, schreibt beim
 * nächsten Mal das Wort in die Datenbank.
 */
export function rohwert(registerSchluessel, spaltenSchluessel, satz) {
  if (registerSchluessel === 'dives') {
    if (spaltenSchluessel in FLASCHE) {
      return satz.tanks?.[0]?.[FLASCHE[spaltenSchluessel]] ?? '';
    }
    if (spaltenSchluessel === 'buddies') return (satz.buddies ?? []).join(', ');
  }
  return satz[spaltenSchluessel] ?? '';
}

/** Was in einer Zelle steht — Anzeigespalten werden lesbar gemacht. */
export function zellwert(registerSchluessel, spaltenSchluessel, satz, anzahl = 0) {
  if (spaltenSchluessel === 'anzahl') return anzahl;
  if (spaltenSchluessel === 'koordinaten') return hatKoordinaten(satz);
  if (registerSchluessel === 'dives') {
    if (spaltenSchluessel === 'start_utc') return datum(satz);
    if (spaltenSchluessel === 'duration_s') return dauer(satz.duration_s);
    if (spaltenSchluessel === 'max_depth_m') return tiefe(satz.max_depth_m);
  }
  const roh = rohwert(registerSchluessel, spaltenSchluessel, satz);
  // Auswahlspalten stehen als Code im Satz und als deutsches Wort in der
  // Tabelle — dieselbe Übersetzung wie im Detail und im Formular.
  if (spalte(registerSchluessel, spaltenSchluessel)?.art === 'auswahl') return wort(roh) ?? '';
  return roh;
}

export function baueZeilen(registerSchluessel, saetze, tauchgaenge = [], spalten = null) {
  const def = register(registerSchluessel);
  if (!def) return [];
  const anzahl = zaehle(registerSchluessel, saetze, tauchgaenge);
  const gewaehlt = (spalten ?? def.spalten).map((s) => s.schluessel);
  // Die Pflichtspalten werden immer mitgerechnet, auch wenn ihre Gruppe gerade
  // ausgeblendet ist: "nur unvollständige" fragt, wo Arbeit aussteht — nicht,
  // welche Gruppe eingeblendet ist. Ohne sie sähe der Filter überall ein
  // `undefined` und meldete jede Zeile.
  const zuRechnen = [...gewaehlt, ...def.pflicht.filter((s) => !gewaehlt.includes(s))];

  const zeilen = saetze.map((satz) => ({
    satz,
    werte: Object.fromEntries(zuRechnen.map((s) =>
      [s, zellwert(registerSchluessel, s, satz, anzahl.get(satz.id) ?? 0)])),
  }));

  if (registerSchluessel === 'dives') {
    // Die jüngsten zuerst — wie in der Liste, damit man sich nicht umgewöhnt.
    return zeilen.sort((a, b) => (b.satz.dive_number ?? 0) - (a.satz.dive_number ?? 0));
  }
  return zeilen.sort((a, b) =>
    String(a.satz.name ?? '').localeCompare(String(b.satz.name ?? ''), 'de'));
}

/**
 * Fehlt an dieser Zeile etwas?
 *
 * Die Pflichtspalten je Register stehen in der Registerdefinition. Die
 * Koordinatenspalte ist ein Sonderfall: Dort steht bereits ein Wahrheitswert,
 * und `false` bedeutet "fehlt" — nicht "ist leer".
 */
export function istUnvollstaendig(zeile, registerSchluessel) {
  const def = register(registerSchluessel);
  if (!def) return false;
  return def.pflicht.some((schluessel) => {
    const wert = zeile.werte[schluessel];
    if (schluessel === 'koordinaten') return wert !== true;
    return wert === '' || wert === null || wert === undefined;
  });
}

/**
 * Alle Werte einer Zeile — auch die der ausgeblendeten Feldgruppen.
 *
 * Was nicht angezeigt wird, steht nicht in `werte`. Die Suche darf davon nicht
 * abhängen: "Rotes Meer" muss die Reise finden, auch während die Gruppe
 * *Tauchgang* eingeblendet ist. Gerechnet wird nur, was fehlt — und nur, wenn
 * überhaupt gesucht wird.
 */
function alleWerte(zeile, def) {
  if (!def) return Object.values(zeile.werte);
  return def.spalten.map((s) => (s.schluessel in zeile.werte
    ? zeile.werte[s.schluessel]
    : zellwert(def.schluessel, s.schluessel, zeile.satz, zeile.werte.anzahl ?? 0)));
}

export function siebe(zeilen, registerSchluessel, { text = '', nurUnvollstaendige = false } = {}) {
  const def = register(registerSchluessel);
  const gesucht = String(text).trim().toLowerCase();
  return zeilen.filter((zeile) => {
    if (nurUnvollstaendige && !istUnvollstaendig(zeile, registerSchluessel)) return false;
    if (!gesucht) return true;
    return alleWerte(zeile, def)
      .some((wert) => String(wert ?? '').toLowerCase().includes(gesucht));
  });
}

/** Spalten, die auf einen Stammsatz verweisen: Spalte -> Speicher und Feld. */
const VERWEISE = {
  site_name: { speicher: SPEICHER.sites, feld: 'site_id' },
  trip_name: { speicher: SPEICHER.trips, feld: 'trip_id' },
};

/**
 * Darf diese Spalte diesen Wert bekommen?
 *
 * Gemeinsam für die einzelne Zelle und die Sammeländerung — sonst gälte die
 * Sperre nur an einer der beiden Stellen, und genau die andere richtet den
 * Schaden reihenweise an.
 */
function pruefeWert(registerSchluessel, spaltenSchluessel) {
  const def = spalte(registerSchluessel, spaltenSchluessel);
  if (!def) throw new Error(`Unbekannte Spalte: ${spaltenSchluessel}`);
  if (!def.bearbeitbar) {
    throw new Error(`Die Spalte "${def.beschriftung}" ist in der Tabelle gesperrt.`);
  }
  return def;
}

function pruefeNichtLeer(def, wert) {
  if (def.nichtLeer && String(wert ?? '').trim() === '') {
    throw new Error(`"${def.beschriftung}" darf nicht leer sein — ein Stammsatz `
      + 'ohne Namen ist in der Tabelle nicht mehr auffindbar. Zum Entfernen '
      + 'stattdessen zusammenführen.');
  }
}

/**
 * Welche Spalten taugen für eine Sammeländerung?
 *
 * Nicht jede bearbeitbare: Der Name eines Stammsatzes gehört zu genau einem
 * Satz. Ihn auf viele anzuwenden ergäbe zweiundzwanzig Plätze namens "Sharm",
 * ihn leer anzuwenden namenlose Sätze. Beim Register "Buddys" bleibt damit
 * nichts übrig — die Ansicht blendet dort Leiste und Häkchen ganz aus.
 */
/**
 * Welche Spalte trägt den Namen der Karte?
 *
 * Auf einem Telefon wird aus der Tabelle je Satz eine Karte. Eine Karte
 * braucht eine Überschrift, und die ist bei jedem Register eine andere: Bei
 * Plätzen, Reisen, Buddys und Ausrüstung heisst sie `name`, bei den
 * Tauchgängen ist es der Platz — die Nummer allein sagt nichts, und das Datum
 * ist die zweite Auskunft, nicht die erste.
 */
const KARTENTITEL = { dives: 'site_name' };

/** Was unter der Überschrift steht, klein und einzeilig. */
const KARTENUNTERZEILE = { dives: ['dive_number', 'start_utc'] };

/**
 * Die Spalten in Überschrift, Unterzeile und Felder aufteilen.
 *
 * Ohne DOM, damit die Aufteilung prüfbar bleibt: Sie entscheidet, was auf
 * einem Telefon zuerst zu lesen ist, und das ist eine inhaltliche Frage und
 * keine der Gestaltung. Jede Spalte kommt genau einmal vor — sonst stünde ein
 * Wert zweimal auf derselben Karte.
 */
export function kartenaufbau(registerSchluessel, spalten) {
  const gewuenscht = KARTENTITEL[registerSchluessel] ?? 'name';
  const titel = spalten.find((s) => s.schluessel === gewuenscht) ?? spalten[0] ?? null;
  const unter = (KARTENUNTERZEILE[registerSchluessel] ?? [])
    .map((schluessel) => spalten.find((s) => s.schluessel === schluessel))
    .filter(Boolean)
    .filter((s) => s.schluessel !== titel?.schluessel);
  const belegt = new Set([titel?.schluessel, ...unter.map((s) => s.schluessel)]);
  return { titel, unter, felder: spalten.filter((s) => !belegt.has(s.schluessel)) };
}

/**
 * Die Zeile unter dem Namen einer Karte.
 *
 * Eine aufgeklappte Karte füllt auf einem Telefon den ganzen Schirm — zwölf
 * Plätze sind dann zwölf Bildschirme, und in elf davon steht nichts, was man
 * gerade sucht. Zugeklappt zeigt die Karte den Namen und diese Zeile; das
 * genügt zum Wiederfinden, und aufgeklappt wird, was bearbeitet werden soll.
 *
 * Genommen werden die ersten gefüllten Spalten in ihrer Reihenfolge — leere
 * überspringt sie, denn eine Zeile aus Gedankenstrichen sagt nichts. Eine
 * blosse Zahl bekommt ihre Beschriftung davor: „11" allein ist kein Hinweis.
 * Davor und nicht dahinter, weil die Beschriftung nicht immer eine Einheit
 * ist — „11 Tauchgänge" liest sich gut, „4 Bewertung" ist kein Deutsch.
 * „Bewertung 4" und „Tauchgänge 11" sind beide nie falsch.
 */
export function kartenkurz(aufbau, werte, grenze = 3) {
  const teile = [];
  for (const spalte of aufbau.felder) {
    if (teile.length >= grenze) break;
    const wert = werte[spalte.schluessel];
    const text = wert === null || wert === undefined ? '' : String(wert).trim();
    // '–' ist die Anzeige einer nicht gesetzten Marke, '—' ein leeres Feld.
    if (text === '' || text === '–' || text === '—') continue;
    teile.push(/^\d+([.,]\d+)?$/.test(text) ? `${spalte.beschriftung} ${text}` : text);
  }
  return teile.join(' · ');
}

export function sammelSpalten(registerSchluessel) {
  return (register(registerSchluessel)?.spalten ?? [])
    .filter((s) => s.bearbeitbar && !s.nichtLeer);
}

/**
 * Darf dieses Feld geleert werden?
 *
 * Grundregel: Nicht-gepflegte Felder kommen aus dem Import und nehmen einen
 * neuen Wert an, aber keinen leeren — der alte käme aus `raw/` nie zurück.
 *
 * Die eine Ausnahme ist die Rücknahme eines Sammelvorgangs
 * (`optionen.wiederherstellung`). Sie schreibt nicht irgendetwas Leeres,
 * sondern genau den Stand, der Sekunden zuvor dastand — und dass ihn seither
 * niemand angefasst hat, hat der Zeitstempel-Riegel `erwartet` bereits
 * festgestellt. Ohne diese Ausnahme wäre jedes Füllen eines leeren
 * Importfeldes unumkehrbar, also die Hälfte dessen, was das Sammelformular
 * anbietet.
 *
 * Die Ausnahme gilt nur hier. Der Namensschutz eines Stammsatzes
 * (`pruefeNichtLeer`) bleibt auf jedem Weg absolut.
 */
function pruefeLeerbar(registerSchluessel, def, istLeer, wiederherstellung = false) {
  if (registerSchluessel !== 'dives' || !istLeer || def.gepflegt) return;
  if (wiederherstellung) return;
  throw new Error(`"${def.beschriftung}" kann nicht geleert werden — der Wert kommt aus dem Import.`);
}

function pruefeSammelbar(def) {
  if (def.nichtLeer) {
    throw new Error(`"${def.beschriftung}" lässt sich nur je Satz ändern, `
      + 'nicht für viele auf einmal.');
  }
}

/**
 * Vorab prüfen, ob eine Sammeländerung dieser Spalte überhaupt erlaubt ist.
 *
 * Damit die Ansicht die Rückfrage "auf ... setzen?" gar nicht erst stellt: Wer
 * eine Sperre bestätigen soll, die danach doch greift, hält die Meldung für
 * eine Panne.
 *
 * Seit die Sammelleiste über `schreibeSammel()` schreibt, ist dies die einzige
 * Stelle, die eine gesperrte Spalte oder die Namensspalte für den ganzen
 * Vorgang abweist — `schreibeSammel()` prüft je Satz und würde nur zwanzig
 * gleichlautende Fehler sammeln.
 */
export function pruefeSammelwert(registerSchluessel, spaltenSchluessel, wert) {
  const def = pruefeWert(registerSchluessel, spaltenSchluessel);
  pruefeSammelbar(def);
  pruefeNichtLeer(def, wert);
}

/**
 * Eine einzelne Zelle schreiben.
 *
 * Wirft, statt still zu scheitern: Die Ansicht setzt die Zelle dann auf den
 * alten Wert zurück und zeigt die Meldung. Eine Änderung, die halb ankommt,
 * wäre schlimmer als eine, die sichtbar abgelehnt wird.
 *
 * `optionen.wiederherstellung` sagt: Dieser Schreibvorgang stellt einen Stand
 * wieder her, der Sekunden zuvor genau so dastand — siehe `pruefeLeerbar()`.
 */
export async function schreibeZelle(db, registerSchluessel, satz, spaltenSchluessel, wert,
  optionen = {}) {
  const def = pruefeWert(registerSchluessel, spaltenSchluessel);
  pruefeNichtLeer(def, wert);

  const istLeer = String(wert ?? '').trim() === '';
  pruefeLeerbar(registerSchluessel, def, istLeer, optionen.wiederherstellung);

  const speicher = SPEICHER[registerSchluessel];
  const neu = { ...satz };
  let neuerStammsatz = false;

  if (spaltenSchluessel === 'dive_number') {
    const nummer = Number(wert);
    const alle = await alleTauchgaenge(db);
    if (vergebeneNummern(alle, satz.id).includes(nummer)) {
      throw new Error(`Nummer ${nummer} ist schon vergeben.`);
    }
    neu.dive_number = nummer;
  } else if (spaltenSchluessel in VERWEISE) {
    const { speicher: stammSpeicher, feld } = VERWEISE[spaltenSchluessel];
    if (istLeer) {
      // Gepflegte Felder werden hier geprüft (via der Guard oben);
      // bei Leerung: beide Felder auf null setzen (die Verknüpfung aufheben).
      neu[feld] = null;
      neu[spaltenSchluessel] = null;
    } else {
      const stamm = await findeOderLege(db, stammSpeicher, wert, optionen);
      neuerStammsatz = stamm.neu;
      neu[feld] = stamm.id;
      neu[spaltenSchluessel] = String(wert ?? '').trim();
    }
  } else if (spaltenSchluessel === 'buddies') {
    neu.buddies = String(wert ?? '').split(',').map((n) => n.trim()).filter(Boolean);
  } else if (spaltenSchluessel in FLASCHE) {
    // Die Flaschenwerte liegen in tanks[0]. Weitere Flaschen bleiben stehen;
    // 66 Tauchgaenge im Bestand haben mehr als eine.
    const flasche = { ...(satz.tanks?.[0] ?? {}) };
    const wertImSatz = wert === '' || wert === null ? null
      : (def.art === 'zahl' ? Number(wert) : wert);
    flasche[FLASCHE[spaltenSchluessel]] = wertImSatz;
    neu.tanks = [flasche, ...(satz.tanks ?? []).slice(1)];
  } else if (def.art === 'zahl') {
    neu[spaltenSchluessel] = wert === '' || wert === null ? null : Number(wert);
  } else {
    neu[spaltenSchluessel] = wert === '' ? null : wert;
  }

  await speichere(db, speicher, neu, optionen);
  return { satz: neu, neuerStammsatz };
}


/**
 * Eine gerechnete Sammelbearbeitung schreiben.
 *
 * Je Satz mehrere Felder, nacheinander: `schreibeZelle` gibt den ergänzten
 * Satz zurück, und nur wer damit weiterarbeitet, behält das vorige Feld. Zwei
 * Schreibvorgänge vom selben Ausgangsstand löschen einander.
 *
 * Ein Fehler betrifft immer nur seinen Satz — die übrigen laufen weiter,
 * berichtet wird am Ende.
 *
 * `optionen.erwartet` ist der Riegel der Rücknahme: Wer einen Satz seit dem
 * Sammelvorgang von Hand geändert hat, soll diese Arbeit nicht verlieren.
 * Passt der Zeitstempel nicht mehr — oder gibt es den Satz nicht mehr, oder
 * nennt ihn die Erwartung gar nicht —, wird er übergangen und gezählt.
 *
 * `optionen.wiederherstellung` reicht die Rücknahme durch bis
 * `pruefeLeerbar()`; nur sie darf ein Importfeld wieder leeren.
 */
export async function schreibeSammel(db, registerSchluessel, aenderungen, optionen = {}) {
  const melde = optionen.melde ?? null;
  const erwartet = optionen.erwartet ?? null;
  const fehler = [];
  const staende = [];
  let geschrieben = 0;
  let uebersprungen = 0;
  const optionenOhneMelde = { ...optionen, melde: null };
  delete optionenOhneMelde.erwartet;

  const speicher = SPEICHER[registerSchluessel];
  const zeitstempel = async () =>
    new Map((await alleAus(db, speicher)).map((s) => [s.id, s.geaendert]));

  // Einmal lesen statt je Satz: Bei 995 markierten Sätzen wären das sonst
  // 995 einzelne Anfragen. Nur nötig, wenn wirklich verglichen wird.
  const stand = erwartet ? await zeitstempel() : null;
  const geschriebeneIds = [];

  for (const [i, { satz, felder }] of aenderungen.entries()) {
    // Ein Satz, den die Erwartung gar nicht nennt, gilt als Abweichung. Sonst
    // verglichen zwei `undefined` miteinander, und ein längst gelöschter Satz
    // käme neben seinem Grabstein zurück in den Speicher.
    if (erwartet && (!Object.hasOwn(erwartet, satz.id)
      || stand.get(satz.id) !== erwartet[satz.id])) {
      uebersprungen += 1;
      if (melde) melde(i + 1, aenderungen.length);
      continue;
    }
    try {
      let jetzt = satz;
      for (const { schluessel, wert } of felder) {
        const { satz: neu } = await schreibeZelle(db, registerSchluessel, jetzt,
          schluessel, wert, optionenOhneMelde);
        jetzt = neu;
      }
      geschrieben += 1;
      geschriebeneIds.push(satz.id);
    } catch (f) {
      fehler.push({ id: satz.id, text: f.message });
    }
    if (melde) melde(i + 1, aenderungen.length);
  }

  // Die Zeitstempel entstehen erst in `speichere()` und stehen nur in der
  // Datenbank, nicht im zurückgegebenen Satz — also einmal nachlesen.
  if (geschriebeneIds.length) {
    const frisch = await zeitstempel();
    for (const id of geschriebeneIds) {
      staende.push({ id, geaendert: frisch.get(id) ?? null });
    }
  }

  return { geschrieben, fehler, uebersprungen, staende };
}
